import type { ReactNode } from 'react';
import clsx from 'clsx';
import type { ModuleStatus } from './ModuleRegistry';

interface PreviewShellProps {
  children: ReactNode;
  moduleId?: string;
  status?: ModuleStatus;
}

/**
 * Isolated module wrapper for `/preview/<id>`.
 *
 * - On mobile (≤500px) the inner frame fills the viewport so it matches Telegram WebView.
 * - On desktop ≥500px we render an iPhone-14-shaped frame (390×844) centered on the page,
 *   so a designer can review the module without a phone.
 */
export function PreviewShell({ children, moduleId, status }: PreviewShellProps) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface-main">
      <div
        className={clsx(
          'relative overflow-hidden bg-surface-main',
          'sm:rounded-[40px] sm:border sm:border-border-hairline sm:shadow-final-glow',
        )}
        style={{
          width: 'min(390px, 100vw)',
          height: 'min(844px, 100dvh)',
        }}
      >
        {children}
        {moduleId && (
          <div
            className="pointer-events-none absolute right-sp-2 top-sp-2 z-50 hidden sm:flex sm:flex-col sm:items-end sm:gap-sp-1 font-ui text-caption text-text-muted"
            aria-hidden
          >
            <span className="rounded-full border border-border-hairline px-sp-2 py-[2px] uppercase tracking-[0.2em]">
              {status ?? 'draft'}
            </span>
            <span>{moduleId}</span>
          </div>
        )}
      </div>
    </div>
  );
}
